import React, { useState } from 'react';
import OperationSuiviMarche from '../../../composants/OperationSuiviMarche/OperationSuiviMarche';
import OperationConsultation from './OperationConsultation';
import OperationOffre from './OperationOffre';
import OperationNotification from './OperationNotification';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import CommandeEvaluationTab from '../../../composants/AvisPrestataire/CommandeEvaluationTab';
import TabMessagerie from '../../../composants/TabMessagerie/TabMessagerie';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { useOperationBCActions } from '../hooks/useOperationBCActions';
import { computeNextTabAction, NextTabRender } from '../domain/operationNextTab';
import { TabDescriptor, OperationBC, StatusId } from '../types';
import { MessageChannel } from '../../../services/messagerie/useUnreadCounts';
import { reloadToTab } from '../../../utils/tabPreference';
import { formatDate } from '../../../utils/entityUtils';

interface OperationContentProps {
  operation: OperationBC;
  setOperation: React.Dispatch<React.SetStateAction<OperationBC | null>>;
  refetch: () => void;
  descriptors: TabDescriptor[];
  activeTab: TabDescriptor | null;
  // Bascule d'onglet (setActiveIndex du hook) : elle seule sait refuser une destination grisee.
  onNavigateTab: (index: number) => void;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  acceptCGV: boolean;
  setAcceptCGV: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: React.RefObject<HTMLDivElement>;
  // Vide le badge messagerie de l'onglet apres le mark-read serveur (declenche par TabMessagerie).
  onMarkRead?: (channel: MessageChannel) => void;
}

// Routeur d'onglet par descripteur type (kind/rang). Chaque chemin retourne un noeud
// non nul : le bug "rendu blanc" de l'ancien switch/break ne peut pas se reproduire.
const OperationContent: React.FC<OperationContentProps> = ({
  operation,
  setOperation,
  refetch,
  descriptors,
  activeTab,
  onNavigateTab,
  errors,
  setErrors,
  acceptCGV,
  setAcceptCGV,
  printRef,
  onMarkRead,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [showConsultationSignModal, setShowConsultationSignModal] = useState(false);
  // Rang dont on lance la consultation : il determine l'onglet d'arrivee apres signature (Offre du
  // MEME rang). Sans lui, `?tab=offre` viserait toujours le rang 1, tous les onglets Offre partageant
  // ce kind — d'ou la resolution par cle `offre-<rang>`.
  const [rangConsultationSignee, setRangConsultationSignee] = useState(1);
  const { accepter, refuser } = useOperationBCActions(operation, refetch);

  // La destination et la bascule sont resolues ICI : les feuilles ignorent les descripteurs et les
  // index. On leur passe la DECISION, pas un noeud deja rendu — leur barre d'actions ne s'affiche
  // que si elle a quelque chose a montrer, il leur faut donc `visible` pour trancher.
  // `hasWorkflowAction` reste a leur charge : elles seules savent si leur barre propose deja un
  // orange qui fait avancer le workflow (role signataire, chrono expire).
  const nextTabFor = (hasWorkflowAction: boolean): NextTabRender => {
    const next = computeNextTabAction(descriptors, activeTab, { hasWorkflowAction });
    return { ...next, onClick: () => onNavigateTab(next.index) };
  };

  // La messagerie est un composant PARTAGE : son contrat prend un noeud, pas une decision.
  const messagerieNext = nextTabFor(false);

  const renderActiveTab = (): React.ReactNode => {
    if (!activeTab) return null;

    switch (activeTab.kind) {
      case 'historique':
        return <OperationSuiviMarche operation={operation} />;

      case 'consultation':
        return (
          <OperationConsultation
            commande={operation}
            rang={(activeTab.rang ?? 1) as 1 | 2 | 3}
            errors={errors}
            setErrors={setErrors}
            setCommande={setOperation}
            handleLancerConsultation={(rangLance: number) => {
              setRangConsultationSignee(rangLance);
              setShowConsultationSignModal(true);
              return Promise.resolve();
            }}
            handleAccepterCommande={accepter}
            handleRefuserCommande={refuser}
            acceptCGV={acceptCGV}
            setAcceptCGV={setAcceptCGV}
            printRef={printRef}
            nextTabFor={nextTabFor}
          />
        );

      case 'offre':
        return (
          <OperationOffre
            commande={operation}
            rang={activeTab.rang ?? 1}
            acceptCGV={acceptCGV}
            setAcceptCGV={setAcceptCGV}
            printRef={printRef}
            nextTabFor={nextTabFor}
          />
        );

      case 'notification':
        // Actif uniquement au statut Valide (7), gate en domaine (operationTabs).
        // Aucune action orange de workflow ici : le telechargement du bon de commande signe
        // n'en est pas une, il ne fait pas avancer l'operation.
        return <OperationNotification operation={operation} nextTab={nextTabFor(false)} />;

      // Messagerie privee BDC : composant partage TabMessagerie parametre par apiBase='/bdc'.
      // L'onglet n'est cliquable qu'au statut Valide (7), gate en domaine (operationTabs).
      case 'messagerie': {
        // Bandeau vert "messagerie active" (parite MS) : le bon de commande est signe/accepte au
        // statut Valide (7). Date d'activation = signature du rang retenu (updated_at du rang au
        // statut Valide), meme cle que le case 'evaluation'.
        const rangAccepteMsg = (operation.commandes || []).find((c) => c.id_status === StatusId.Valide);
        const messagerieActiveSince =
          operation.id_status === StatusId.Valide
            ? rangAccepteMsg?.updated_at
              ? formatDate(rangAccepteMsg.updated_at)
              : null
            : undefined;
        return (
          <TabMessagerie
            operationId={operation.id}
            operationStatus={operation.id_status ?? 0}
            channel="messagerie"
            apiBase="/bdc"
            onMarkRead={onMarkRead}
            messagerieActiveSince={messagerieActiveSince}
            nextTabButton={
              <NextTabButton
                label={messagerieNext.label}
                hidden={!messagerieNext.visible}
                onClick={messagerieNext.onClick}
              />
            }
          />
        );
      }

      case 'evaluation': {
        // Avis cree en amont (hook a la validation + backfill) ; ici on l'affiche seulement.
        // Cle de lecture = la commande validee (acceptee = 7) de l'operation.
        const retenu = (operation.commandes || []).find((c) => c.id_status === StatusId.Valide);
        return (
          <>
            <CommandeEvaluationTab
              typeCommande="BC"
              commandeId={retenu?.id ?? 0}
              validated={Boolean(retenu?.id)}
            />
            <CommandeEvaluationTab
              typeCommande="BC"
              commandeId={retenu?.id ?? 0}
              validated={Boolean(retenu?.id)}
              cible="acheteur"
            />
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {renderActiveTab()}
      {showConsultationSignModal && (
        <SignatureFlow
          operationId={operation.id}
          commandeId={0}
          resourcePrefix="bdc"
          // Pre-remplit le nom avec le signataire DESIGNE dans le descriptif administratif : c'est a
          // ca que sert ce champ. Sans lui, SignatureFlow retombait sur le compte connecte, et le nom
          // saisi en amont ne servait a rien. Le signataire reste modifiable dans la modale.
          // Parite MS (OperationMarche passe deja initialNomComplet).
          initialNomComplet={operation.signataire_commande || operation.formulaire?.signataire}
          signerUrl={`${apiUrl}/bdc/signature/${operation.id}/consultation-signer`}
          successTitle="Consultation lancée"
          successBody="Votre signature a été apposée sur le document provisoire. Le prestataire a été informé par email."
          onClose={() => setShowConsultationSignModal(false)}
          onSigned={() => {
            setShowConsultationSignModal(false);
            // La consultation vient d'etre lancee : on atterrit sur l'Offre de CE rang, la ou la
            // reponse du prestataire est attendue. Rechargement conserve (etat frais : document
            // provisoire signe, statut, historique).
            // Repli sur la Consultation du meme rang : l'onglet Offre exige des articles ou une
            // proposition (offreDisponible), donc il est encore ferme quand l'acheteur a laisse le
            // titulaire chiffrer, ou en BC transfere. Sans ce repli on retombait sur l'Historique.
            reloadToTab([`offre-${rangConsultationSignee}`, `consultation-${rangConsultationSignee}`]);
          }}
        />
      )}
    </>
  );
};

export default OperationContent;
